import { useMemo, useState, MouseEvent } from 'react';
import { useDispatch, batch } from 'react-redux';
import { useTranslator } from '@picsart/rc/services/localization';
import { Segment, Segments, SegmentsSizesSM } from '@picsart/segments';
import { TabPanel } from '@picsart/tabs';
import Dynamic from '@picsart/editorlayout/Dynamic';
import DynamicHeader from '@picsart/editorlayout/DynamicHeader';
import DynamicBody from '@picsart/editorlayout/DynamicBody';
import classNames from 'classnames';

import batchSlice from 'store/batch';
import useUnMount from 'hooks/common/useUnMount';
import useMount from 'hooks/common/useMount';
import {
  BATCH_ACTIONS,
  BATCH_TOOLS,
  batchToolsList,
} from 'pages/Batch/configs';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import analytics from 'pages/Batch/utils/analytics';
import { RemoveBgTabs } from 'configs/constants';
import useMemoSelector from 'hooks/useMemoSelector';
import { getBackgrounds, getShowAsCategoryCardData } from 'store/selectors/general';
import useHeaderStyles from 'pages/Batch/styles';
import { cmsBackgroundsToShopBackgroundsAdapter } from 'pages/Batch/sections/Sidebar/RemoveBgTool/helpers';
import { getCropTemplate } from 'store/batch/selectors';
import RemoveBgColors from 'pages/Batch/sections/Sidebar/RemoveBgTool/RemoveBgColors';
import ResizeBySocial from 'pages/Batch/sections/Sidebar/ResizeTool/ResizeBySocial/ResizeBySocial';
import Texture from 'pages/Batch/sections/Sidebar/RemoveBgTool/Texture';

import { IProcessingTool } from '../types';

import useStyles from './styles';

const RemoveBgResizeTool = ({ inProgress }: IProcessingTool) => {
  const classes = useStyles();
  const headerClasses = useHeaderStyles();
  const translate = useTranslator();
  const { process, setBottomSheetCustomHeight, bottomSheetCustomHeight } = useBatchData();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState<RemoveBgTabs>(RemoveBgTabs.color);

  const { cmsBackgrounds } = useMemoSelector(state => ({
    cmsBackgrounds: getBackgrounds(state),
    showAsCategoryCardData: getShowAsCategoryCardData(state),
    selectedCard: getCropTemplate(state),
  }));

  const backgrounds = useMemo(() => cmsBackgroundsToShopBackgroundsAdapter(cmsBackgrounds), [cmsBackgrounds]);

  const handleTabChange = (ev: MouseEvent<HTMLElement>) => {
    const { tab } = ev.currentTarget.dataset;

    const bottomSheetHeight = batchToolsList.find(el => el.type === BATCH_TOOLS.removeBG_resize)?.bottomSheetHeight || bottomSheetCustomHeight;
    setBottomSheetCustomHeight(bottomSheetHeight);
    setActiveTab(Number(tab));
  };

  useMount(() => {
    analytics.openEvent(BATCH_TOOLS.removeBG_resize);
    process(BATCH_ACTIONS.removeBackground, {});
  });

  useUnMount(() => {
    batch(() => {
      dispatch(batchSlice.actions.setActiveTexture(null));
      dispatch(batchSlice.actions.setActiveColor(null));
    });
  });

  return (
    <Dynamic
      header={(
        <DynamicHeader
          className={headerClasses.customHeader}
          title={translate('Batch Remove BG')}
        >
          <Segments size={SegmentsSizesSM}>
            <Segment isActive={activeTab === RemoveBgTabs.color} data-tab={RemoveBgTabs.color} onClick={handleTabChange}>{translate('Color')}</Segment>
            <Segment isActive={activeTab === RemoveBgTabs.texture} data-tab={RemoveBgTabs.texture} onClick={handleTabChange}>{translate('Background')}</Segment>
            <Segment isActive={activeTab === RemoveBgTabs.resize} data-tab={RemoveBgTabs.resize} onClick={handleTabChange}>{translate('Resize')}</Segment>
          </Segments>
        </DynamicHeader>
      )}
      body={(
        <DynamicBody>
          <TabPanel
            className={classNames(classes.tabPanel, {
              [classes.tabDisabled]: inProgress,
            })}
            index={RemoveBgTabs.color}
            value={activeTab}
          >
            <RemoveBgColors inProgress={inProgress} />
          </TabPanel>

          <TabPanel
            className={classNames(classes.tabPanel, {
              [classes.tabDisabled]: inProgress,
            })}
            index={RemoveBgTabs.texture}
            value={activeTab}
          >
            <Texture inProgress={inProgress} backgrounds={backgrounds} />
          </TabPanel>

          <TabPanel
            className={classNames(classes.tabPanel, {
              [classes.tabDisabled]: inProgress,
            })}
            index={RemoveBgTabs.resize}
            value={activeTab}
          >
            <ResizeBySocial isEnabledResizeByPadding={false} />
          </TabPanel>
        </DynamicBody>
      )}
    />
  );
};

export default RemoveBgResizeTool;
